import { createContext, useContext } from 'react';
import noop from 'utils/noop';
import { CampaignEditorView, CampaignStep, CampaignStepsStatesMapper } from '../../types';

export interface EditorContextState {
  campaignSteps: CampaignStepsStatesMapper;
  experimentId: number;
  isExperimentModalOpen: boolean;
  setIsExperimentModalOpen: (isOpen: boolean) => void;
  isAudienceTakeoverOpen: boolean;
  isDisplayPriorityTakeoverOpen: boolean;
  isDisplayUntilTakeoverOpen: boolean;
  canDraftMessaging: boolean;
  setIsAudienceTakeoverOpen: (isOpen: boolean) => void;
  setIsDisplayPriorityTakeoverOpen: (isOpen: boolean) => void;
  setIsDisplayUntilTakeoverOpen: (isOpen: boolean) => void;
  completeCampaignStep: (campaignStep: CampaignStep, isComplete: boolean) => void;
  editCampaignStep: (campaignStep: CampaignStep) => void;
  closeCampaignStep: (campaignStep: CampaignStep) => void;
  viewCampaignStep: (campaignStep: CampaignStep) => void;
  currentEditableStep: CampaignStep | undefined;
  campaignEditorView: CampaignEditorView;
  setCampaignEditorView: (campaignView: CampaignEditorView) => void;
  isAlertHistorySlideOverOpen: boolean;
  setIsAlertHistorySlideOverOpen: (isOpen: boolean) => void;
}

export const DEFAULT_CAMPAIGN_EDITOR_STEP_STATES_MAPPER: CampaignStepsStatesMapper = {
  [CampaignStep.AlertSettings]: { isOpen: false, isEditable: false, isComplete: false },
  [CampaignStep.Audience]: { isOpen: true, isEditable: false, isComplete: false },
  [CampaignStep.AudienceTriggered]: { isOpen: true, isEditable: false, isComplete: true },
  [CampaignStep.AudienceEligibility]: { isOpen: true, isEditable: false, isComplete: false },
  [CampaignStep.ConversionAndExperiment]: { isOpen: true, isEditable: false, isComplete: false },
  [CampaignStep.Conversions]: { isOpen: true, isEditable: false, isComplete: false },
  [CampaignStep.Delivery]: { isOpen: true, isEditable: false, isComplete: false },
  [CampaignStep.Content]: { isOpen: true, isEditable: false, isComplete: false },
  [CampaignStep.Display]: { isOpen: true, isEditable: false, isComplete: false },
  /** This is never editable */
  [CampaignStep.Summary]: {
    isOpen: false,
    isEditable: false,
    isComplete: true,
  },
};

export const DEFAULT_EDITOR_CONTEXT_STATE: EditorContextState = {
  campaignEditorView: CampaignEditorView.Edit,
  experimentId: 0,
  isExperimentModalOpen: false,
  setIsExperimentModalOpen: noop,
  campaignSteps: DEFAULT_CAMPAIGN_EDITOR_STEP_STATES_MAPPER,
  currentEditableStep: undefined,
  isAudienceTakeoverOpen: false,
  isDisplayPriorityTakeoverOpen: false,
  isDisplayUntilTakeoverOpen: false,
  canDraftMessaging: false,
  setIsAudienceTakeoverOpen: noop,
  setIsDisplayPriorityTakeoverOpen: noop,
  setIsDisplayUntilTakeoverOpen: noop,
  completeCampaignStep: noop,
  editCampaignStep: noop,
  closeCampaignStep: noop,
  viewCampaignStep: noop,
  setCampaignEditorView: noop,
  isAlertHistorySlideOverOpen: false,
  setIsAlertHistorySlideOverOpen: noop,
};

export const EditorContext = createContext<EditorContextState>(DEFAULT_EDITOR_CONTEXT_STATE);
export const useEditorContext = () => useContext(EditorContext);
